import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  DashboardStatsService,
  DashboardStats,
} from "@/lib/services/dashboard-stats";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Search,
  MessageSquare,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  AlertTriangle,
  BarChart3,
  Calendar,
  Zap,
  ArrowRight,
  Eye,
  Download,
  Share,
  Sparkles,
  Briefcase,
  CheckCircle2,
  PieChart,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { PLAN_LIMITS } from "@/lib/constants/plans";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import PlanSelectionModal from "../modals/PlanSelectionModal";
import { usePlansWithFeatures } from "@/hooks/use-plans-data";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";
import { ConnectedUsersCard } from "./ConnectedUsersCard";

export function EnhancedOverview() {
  // usePresenceHeartbeat(); // Handled globally by DashboardLayout
  const { user, subscriptions } = useAuth();
  const navigate = useNavigate();
  const { openDocument } = useDocumentViewer();
  const trialStatus = useTrialStatus();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const { data: plans } = usePlansWithFeatures();

  const loadStats = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const dashboardStats = await DashboardStatsService.getDashboardStats(
        user.id
      );
      setStats(dashboardStats);
    } catch (err) {
      console.error("Error loading dashboard stats:", err);
      setError("Error al cargar las estadísticas");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadStats();
    }
  }, [user, loadStats]);

  const handleView = (activity: any) => {
    const metadata = activity.metadata || {};
    const url = metadata.document_url || metadata.url || metadata.file_url || metadata.result_url;

    if (url) {
      openDocument(String(url), activity.description);
    }
    // No redirect if no URL, as requested
  };

  const handleDownload = (activity: any) => {
    const metadata = activity.metadata || {};
    const url = metadata.document_url || metadata.url || metadata.file_url || metadata.result_url;

    if (url) {
      window.open(String(url), '_blank');
    }
  };

  const getTrendIcon = (value: number) => {
    if (value > 0) return <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />;
    if (value < 0) return <TrendingDown className="h-3.5 w-3.5 text-red-500" />;
    return <Minus className="h-3.5 w-3.5 text-gray-400" />;
  };

  const getTrendColor = (value: number) => {
    if (value > 0) return "text-emerald-600";
    if (value < 0) return "text-red-600";
    return "text-gray-500";
  };

  const getTrendText = (value: number) => {
    if (value > 0) return `+${value}%`;
    if (value < 0) return `${value}%`;
    return "0%";
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-6 space-y-5">
      <ConnectedUsersCard />
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border-gray-100">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-9 w-9 rounded-xl" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 sm:p-6">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-3 sm:p-6">
        <Alert>
          <AlertDescription>No hay datos disponibles</AlertDescription>
        </Alert>
      </div>
    );
  }

  const currentPlan = PLAN_LIMITS[subscriptions?.[0] || "free"] || PLAN_LIMITS.free;

  const statCards = [
    {
      title: "Contratos Analizados",
      icon: FileText,
      value: stats.aiAgentUsage.contractReview,
      trend: 0, // Trends for specific agents not implemented in service yet
      gradient: "from-blue-600 to-indigo-700",
      max: currentPlan.documentos,
      isShared: true,
      sharedValue: stats.aiAgentUsage.contractReview + stats.aiAgentUsage.documentGenerator,
    },
    {
      title: "Documentos Generados",
      icon: FileText,
      value: stats.aiAgentUsage.documentGenerator,
      trend: 0,
      gradient: "from-blue-400 to-blue-600",
      max: currentPlan.documentos,
      isShared: true,
      sharedValue: stats.aiAgentUsage.contractReview + stats.aiAgentUsage.documentGenerator,
    },
    // {
    //   title: "Búsquedas Realizadas",
    //   icon: Search,
    //   value: stats.totalSearches,
    //   trend: stats.searchesIncrease,
    //   gradient: "from-emerald-500 to-teal-600",
    //   max: currentPlan.investigaciones,
    // },
    {
      title: "Consultas Atendidas",
      icon: MessageSquare,
      value: stats.totalConsultations,
      trend: stats.consultationsIncrease,
      gradient: "from-violet-500 to-purple-600",
      max: currentPlan.consultas,
    },
    {
      title: "Procesos Activos",
      icon: Briefcase,
      value: stats.procesosMetrics?.activeProcesses || 0,
      trend: stats.procesosAnalizadosIncrease,
      gradient: "from-fuchsia-500 to-pink-600",
      max: currentPlan.casos || 45,
    },
    {
      title: "Tiempo Ahorrado",
      icon: Clock,
      value: stats.timeSaved,
      trend: stats.timeSavedIncrease,
      gradient: "from-amber-500 to-orange-600",
      max: 1000,
      suffix: "h",
    },
  ];

  const agentUsageItems = [
    { label: "Revisión de Contratos", value: stats.aiAgentUsage.contractReview, max: 20 },
    { label: "Investigación Legal", value: stats.aiAgentUsage.legalResearch, max: 15 },
    { label: "Chatbot Legal", value: stats.aiAgentUsage.legalChatbot, max: 50 },
    { label: "Generador de Documentos", value: stats.aiAgentUsage.documentGenerator, max: 30 },
    { label: "Asistente Consumidor", value: stats.aiAgentUsage.consumerAssistant, max: 25 },
  ];

  const insightItems = [
    {
      label: "Agente Más Usado",
      value: stats.usageInsights.mostUsedAgent === "contractReview"
        ? "Revisión de Contratos"
        : stats.usageInsights.mostUsedAgent === "legalResearch"
          ? "Investigación Legal"
          : stats.usageInsights.mostUsedAgent === "legalChatbot"
            ? "Chatbot Legal"
            : stats.usageInsights.mostUsedAgent === "documentGenerator"
              ? "Generador de Documentos"
              : stats.usageInsights.mostUsedAgent === "consumerAssistant"
                ? "Asistente Consumidor"
                : stats.usageInsights.mostUsedAgent,
      bg: "bg-blue-50",
      color: "text-blue-600",
    },
    {
      label: "Promedio Diario",
      value: stats.usageInsights.averageDailyUsage,
      bg: "bg-emerald-50",
      color: "text-emerald-600",
    },
    {
      label: "Hora Pico",
      value: `${stats.usageInsights.peakUsageHour}:00`,
      bg: "bg-amber-50",
      color: "text-amber-600",
    },
    {
      label: "Crecimiento Semanal",
      value: getTrendText(stats.usageInsights.weeklyGrowth),
      bg: "bg-violet-50",
      color: getTrendColor(stats.usageInsights.weeklyGrowth),
    },
  ];

  const quickActions = [
    {
      label: "Generar Documento",
      icon: FileText,
      path: "/dashboard/agents/document-drafting-v2",
      gradient: "from-blue-500 to-indigo-600",
    },
    {
      label: "Buscar Precedentes",
      icon: Search,
      path: "/dashboard/agents/legal-research-v2",
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      label: "Consultar IA",
      icon: MessageSquare,
      path: "/dashboard/agents/legal-chatbot",
      gradient: "from-violet-500 to-purple-600",
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <ConnectedUsersCard />
      {/* Plan Status Banner */}
      <div className={cn(
        "flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white/40 backdrop-blur-md border border-white/20 p-5 rounded-2xl shadow-sm animate-fade-in",
        trialStatus.isExpired && "bg-red-50/30 border-red-100"
      )}>
        <div className="flex items-center gap-4">
          <div className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center shadow-lg",
            trialStatus.isExpired
              ? "bg-gradient-to-br from-red-500 to-rose-600 shadow-red-600/20"
              : "bg-gradient-to-br from-blue-600 to-indigo-700 shadow-blue-600/20"
          )}>
            {trialStatus.isExpired ? (
              <AlertTriangle className="h-6 w-6 text-white" />
            ) : (
              <Sparkles className="h-6 w-6 text-white" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 leading-tight">Estado del Plan: {currentPlan.label}</h2>
              {trialStatus.isTrial && (
                <Badge className={cn(
                  "border-none px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                  trialStatus.isExpired ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                )}>
                  {trialStatus.isExpired ? "Expirado" : "Trial"}
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {trialStatus.isExpired
                ? "Tu periodo de prueba ha expirado. Por favor, actualiza tu plan para seguir disfrutando de todas las herramientas."
                : trialStatus.isTrial && trialStatus.endsAt
                  ? `Tu periodo de prueba finaliza el ${new Date(trialStatus.endsAt).toLocaleDateString()}`
                  : "Disfruta de todas las herramientas de IA legal"}
            </p>
          </div>
        </div>
        <Button
          variant={trialStatus.isExpired ? "default" : "outline"}
          className={cn(
            "rounded-xl font-bold text-xs px-6",
            trialStatus.isExpired ? "bg-legal-blue hover:bg-legal-navy text-white" : "border-gray-200 hover:bg-gray-50"
          )}
          onClick={() => setShowPlansModal(true)}
        >
          {trialStatus.isExpired ? "Suscribirse Ahora" : "Mejorar Plan"}
        </Button>
      </div>

      <PlanSelectionModal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        plans={plans || []}
      />

      {/* Main Dashboard Content */}
      <div className="space-y-6">
        {/* Main Statistics Cards */}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 transition-all duration-500">
          {statCards.map((card, index) => {
            const Icon = card.icon;
            const displayValue = card.isShared ? card.sharedValue : card.value;
            const remaining = Math.max(0, card.max - displayValue);
            const percent = Math.min((displayValue / card.max) * 100, 100);

            return (
              <Card key={index} className={`border border-gray-100/80 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 animate-fade-in-up stagger-${index + 1}`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm font-medium text-gray-500">
                    {card.title}
                  </CardTitle>
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center shadow-md`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900">
                      {card.value}{card.suffix || ""}
                    </span>
                    {card.trend !== 0 && (
                      <div className="flex items-center gap-1">
                        {getTrendIcon(card.trend)}
                        <span className={`text-xs font-medium ${getTrendColor(card.trend)}`}>
                          {getTrendText(card.trend)}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Este mes</p>
                  <Progress
                    value={percent}
                    className="mt-4 h-1.5 bg-gray-100"
                    indicatorClassName="bg-slate-900"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
                      {card.max === 1000 ? "Total" : `Restante: ${remaining}`}
                    </span>
                    <span className="text-[10px] font-bold text-gray-600">
                      {Math.round(percent)}%
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Additional Metrics */}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {/* Efficiency */}
          <Card className="border border-gray-100/80 animate-fade-in-up stagger-5">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <Activity className="h-4 w-4 text-white" />
                </div>
                Eficiencia General
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { label: "Productividad", value: stats.productivityMetrics.productivity },
                  { label: "Precisión", value: stats.productivityMetrics.accuracy },
                  { label: "Satisfacción", value: stats.productivityMetrics.satisfaction },
                ].map((metric) => (
                  <div key={metric.label}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm text-gray-600">{metric.label}</span>
                      <span className="text-sm font-semibold text-gray-900">{metric.value}%</span>
                    </div>
                    <Progress value={metric.value} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* AI Agent Usage */}
          <Card className="border border-gray-100/80 animate-fade-in-up stagger-6">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-white" />
                </div>
                Uso de Agentes IA
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {agentUsageItems.map((item) => (
                  <div key={item.label}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm text-gray-600">{item.label}</span>
                      <span className="text-sm font-semibold text-gray-900">{item.value}</span>
                    </div>
                    <Progress
                      value={Math.min((item.value / item.max) * 100, 100)}
                      className="h-2"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* AI Insights */}
          <Card className="border border-gray-100/80 animate-fade-in-up stagger-7">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Calendar className="h-4 w-4 text-white" />
                </div>
                Insights de IA
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {insightItems.map((item) => (
                  <div key={item.label} className={`flex justify-between items-center p-3 ${item.bg} rounded-xl`}>
                    <span className="text-sm text-gray-600">{item.label}</span>
                    <span className={`text-sm font-bold ${item.color}`}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Procesos Metrics */}
        <Card className="border border-gray-100/80 animate-fade-in-up stagger-9">
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
                <Briefcase className="h-4 w-4 text-white" />
              </div>
              Estado de los Procesos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {/* Vencimientos y Términos */}
              <div className="flex flex-col items-center p-4 bg-rose-50/50 rounded-xl border border-rose-100">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center mb-2">
                  <Clock className="h-5 w-5 text-rose-600" />
                </div>
                <p className="text-2xl font-black text-rose-700">{stats.procesosMetrics.upcomingDeadlines}</p>
                <p className="text-xs font-bold text-rose-600/80 text-center uppercase mt-1">
                  Vencimientos <br />(Próx. 7 días)
                </p>
              </div>

              {/* Procesos Estancados */}
              <div className="flex flex-col items-center p-4 bg-amber-50/50 rounded-xl border border-amber-100">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <p className="text-2xl font-black text-amber-700">{stats.procesosMetrics.stalledProcesses}</p>
                <p className="text-xs font-bold text-amber-600/80 text-center uppercase mt-1">
                  Inactivos <br />(&gt;30 días)
                </p>
              </div>

              {/* Distribución por Estado */}
              <div className="flex flex-col justify-center p-4 bg-emerald-50/50 rounded-xl border border-emerald-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <PieChart className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-bold text-emerald-700">Distribución</span>
                  </div>
                </div>
                <div className="space-y-3 w-full">
                  <div>
                    <div className="flex justify-between items-center mb-1 text-xs font-medium text-emerald-800">
                      <span>Activos</span>
                      <span>{stats.procesosMetrics.activeProcesses}</span>
                    </div>
                    <Progress
                      value={stats.procesosMetrics.activeProcesses + stats.procesosMetrics.finishedProcesses > 0
                        ? (stats.procesosMetrics.activeProcesses / (stats.procesosMetrics.activeProcesses + stats.procesosMetrics.finishedProcesses)) * 100
                        : 0}
                      className="h-1.5"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1 text-xs font-medium text-emerald-800">
                      <span>Terminados/Archivados</span>
                      <span>{stats.procesosMetrics.finishedProcesses}</span>
                    </div>
                    <Progress
                      value={stats.procesosMetrics.activeProcesses + stats.procesosMetrics.finishedProcesses > 0
                        ? (stats.procesosMetrics.finishedProcesses / (stats.procesosMetrics.activeProcesses + stats.procesosMetrics.finishedProcesses)) * 100
                        : 0}
                      className="h-1.5"
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      {/* <Card className="border border-gray-100/80 animate-fade-in-up stagger-10">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Zap className="h-4 w-4 text-white" />
            </div>
            Acciones Rápidas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {quickActions.map((action) => {
              const Icon = action.icon;
              return (
                <div
                  key={action.path}
                  className="group flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md cursor-pointer transition-all duration-300"
                  onClick={() => navigate(action.path)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && navigate(action.path)
                  }>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{action.label}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
}
